import React from 'react';
import { displaySeconds } from '../../../helpers/string';
import './video.css';

const constraints = {
	video: true,
	audio: { echoCancellation: { exact: true } },
};

const mediaSource = 'MediaSource' in window ? new MediaSource() : null;
let mediaRecorder;
let recordedBlobs;

export class Video extends React.Component {
	constructor(props) {
		super(props);

		this.defaultHeight = 270;
		this.defaultWidth = 360;
		this.defaultReadyTop = 50;
		this.defaultReadyLeft = 150;
		this.defaultReadyFont = 90;

		this.state = {
			error: '',
			started: false,
			recording: false,
			previewing: false,
			hasBlob: false,
			clock: 0,
			readying: null,
			width: this.defaultWidth,
			height: this.defaultHeight,
			initialX: 0,
			readyTop: this.defaultReadyTop,
			readyLeft: this.defaultReadyLeft,
			readyFont: this.defaultReadyFont,
		};

		this.onStart = this.onStart.bind(this);
		this.onPlay = this.onPlay.bind(this);
		this.onRecord = this.onRecord.bind(this);
		this.onReady = this.onReady.bind(this);
		this.onDownload = this.onDownload.bind(this);
		this.onDrag = this.onDrag.bind(this);
		this.onDragStart = this.onDragStart.bind(this);

		mediaSource && mediaSource.addEventListener('sourceopen', this.handleSourceOpen, false);
	}

	onDrag(e) {
		if (e.target.className.indexOf('drag-target') >= 0) {
			const { initialX } = this.state;
			const { clientX } = e;
			if (clientX > 0) {
				let width = this.defaultWidth + clientX - initialX;
				if (width < this.defaultWidth) width = this.defaultWidth;
				const multiplier = width / this.defaultWidth;
				const height = this.defaultHeight * multiplier;
				const readyLeft = this.defaultReadyLeft * multiplier;
				const readyTop = this.defaultReadyTop * multiplier;
				const readyFont = this.defaultReadyFont * multiplier;
				this.setState({ height, width, readyLeft, readyTop, readyFont });
			}
		}
	}

	onDragStart(e) {
		if (e.target.className.indexOf('drag-target') >= 0 && this.state.initialX === 0) {
			this.setState({ initialX: e.clientX });
		}
	}

	async onStart() {
		try {
			const stream = await navigator.mediaDevices.getUserMedia(constraints);
			this.handleSuccess(stream);
		} catch (e) {
			console.error('navigator.getUserMedia error:', e);
		}
	}

	onReady() {
		const ready = this.state.readying;

		if (ready === 0) {
			this.setState({ readying: null });
			this.onRecord();
		} else {
			setTimeout(() => {
				this.setState({ readying: ready === null ? 3 : ready - 1 });
				this.onReady();
			}, 1000);
		}
	}

	onRecord() {
		const { recording } = this.state;
		if (!recording) {
			this.startRecording();
			this.setState({ clock: 0 });
			this.runClock = setInterval(() => this.setState({ clock: this.state.clock + 1 }), 1000);
		} else {
			this.stopRecording();
			clearInterval(this.runClock);
		}

		this.setState({ recording: !recording, previewing: false });
	}

	onPlay() {
		const { value, config } = this.props;
		const { previewing } = this.state;
		this.previewer.src = null;
		this.previewer.srcObject = null;
		this.previewer.src = recordedBlobs
			? window.URL.createObjectURL(new Blob(recordedBlobs, { type: 'video/webm' }))
			: `${config.storage.URL}${config.environment}/${value.streamingUrl}`;
		this.previewer.controls = true;
		previewing ? this.previewer.pause() : this.previewer.play();
		this.setState({ previewing: !previewing });
	}

	onDownload() {
		const blob = new Blob(recordedBlobs, { type: 'video/webm' });
		this.props.onSave(blob);
	}

	startRecording() {
		recordedBlobs = [];
		let options = { mimeType: 'video/webm;codecs=vp9' };
		if (!MediaRecorder.isTypeSupported(options.mimeType)) {
			console.error(`${options.mimeType} is not Supported`);
			options = { mimeType: 'video/webm;codecs=vp8' };
			if (!MediaRecorder.isTypeSupported(options.mimeType)) {
				console.error(`${options.mimeType} is not Supported`);
				options = { mimeType: 'video/webm' };
				if (!MediaRecorder.isTypeSupported(options.mimeType)) {
					console.error(`${options.mimeType} is not Supported`);
					options = { mimeType: '' };
				}
			}
		}

		try {
			mediaRecorder = new MediaRecorder(window.stream, options);
		} catch (e) {
			console.error('Exception while creating MediaRecorder:', e);
			return;
		}

		mediaRecorder.onstop = event => {};

		mediaRecorder.ondataavailable = this.handleDataAvailable;
		mediaRecorder.start(10); // collect 10ms of data
		this.setState({ recording: true });
	}

	stopRecording() {
		mediaRecorder.stop();
		this.onDownload();
		this.setState({ recording: false, hasBlob: true });
	}

	handleSourceOpen(event) {
		mediaSource && mediaSource.addSourceBuffer('video/webm; codecs="vp8"');
	}

	handleDataAvailable(event) {
		if (event.data && event.data.size > 0) {
			recordedBlobs.push(event.data);
		}
	}

	handleSuccess(stream) {
		this.setState({ started: true });
		window.stream = stream;
		this.recorder.srcObject = stream;
	}

	componentDidMount() {
		window.addEventListener('drag', this.onDrag);
		window.addEventListener('dragstart', this.onDragStart);

		if (!mediaSource) return;
		this.onStart();

		this.previewer.addEventListener('play', event => {
			console.log(
				'The Boolean paused property is now false. Either the ' +
					'play() method was called or the autoplay attribute was toggled.',
			);
		});
	}

	componentWillUnmount() {
		if (!mediaSource) return;
		const stream = this.recorder.srcObject;
		if (stream) {
			const tracks = stream.getTracks();

			tracks.forEach(function (track) {
				track.stop();
			});

			this.recorder.srcObject = null;
		}
	}

	componentWillReceiveProps(newProps) {
		const { value, config } = this.props;
		if (newProps.videoPosition) {
			this.previewer.src = null;
			this.previewer.srcObject = null;
			this.previewer.src = `${config.storage.URL}${config.environment}/${value.streamingUrl}`;
			this.previewer.controls = true;
			this.previewer.currentTime = newProps.videoPosition;
			this.previewer.play();
			this.setState({ previewing: true });
		}
	}

	render() {
		const { value } = this.props;
		const { started, recording, previewing, hasBlob, clock, readying } = this.state;

		if (!mediaSource) return <div>Sorry but this functionality is not currently supported on your device.</div>;

		return (
			<div className="video-recorder" style={{ width: `${this.state.width}px` }}>
				<video
					style={{ display: previewing && readying === null ? '' : 'none' }}
					ref={elm => (this.previewer = elm)}
					playsInline
				/>
				<div
					className="recording-icon"
					style={{
						display: !previewing && (clock % 2 === 0 || !recording) ? '' : 'none',
					}}
				>
					<i className={`fa fa-2x ${recording ? 'fa-circle-play' : 'fa-circle-stop'} text-danger`} />
				</div>
				{readying !== null && (
					<div
						className="readying"
						style={{
							top: `${this.state.readyTop}px`,
							left: `${this.state.readyLeft}px`,
							fontSize: `${this.state.readyFont}px`,
						}}
					>
						{readying}
					</div>
				)}
				<video
					style={{
						display: !previewing || readying ? '' : 'none',
						height: `${this.state.height}px`,
					}}
					ref={elm => (this.recorder = elm)}
					playsInline
					autoPlay
					muted
				/>

				<div className="video-buttons">
					{!started ? (
						<button className="btn btn-primary mr10" onClick={this.onStart} id="start">
							Start camera
						</button>
					) : (
						<span>
							{recording && (
								<span>
									<button className="btn btn-primary mr5" onClick={this.onRecord} title="Stop">
										<i className="fa fa-stop" />
									</button>
								</span>
							)}
							{!recording && (
								<button className="btn btn-primary mr5 btn-danger" onClick={this.onReady} title="Record">
									<i className="fa fa-circle-dot" />
								</button>
							)}
							<button
								className="btn btn-primary mr5"
								onClick={this.onPlay}
								id="play"
								disabled={recording || (!value && !hasBlob)}
								title="Preview"
							>
								<i className={`fa fa-${previewing ? 'stop' : 'play'}`} />
							</button>
						</span>
					)}
				</div>
				<div className="video-time">{displaySeconds(clock)}</div>
				<div className="video-drag">
					<i ref={elm => (this.dragger = elm)} draggable={true} className="fa fa-angle-double-down drag-target" />
				</div>
			</div>
		);
	}
}

export default Video;
